import * as React from 'react';
import {AccessibilityRole, Animated, Platform, Text} from 'react-native';
import {createIconSetFromIcoMoon} from 'react-native-vector-icons';
import PropTypes from 'prop-types';

import testProperties from '/util/testing/testProperties';

import icoMoonConfig from './ico-moon-config.json';
import {styles} from './Icon.style';

const ConnectIcon = Animated.createAnimatedComponent(
  createIconSetFromIcoMoon(icoMoonConfig, 'icomoon', 'icomoon.ttf'),
);

// Function to get the Unicode character for an icon by name
const getIconUnicode = (name: string): string => {
  const icon = icoMoonConfig.icons.find(
    (icon: any) => icon.properties.name === name,
  );
  if (icon) {
    return String.fromCharCode(icon.properties.code);
  }
  return '';
};

interface IconProps {
  name: string;
  style: {};
  accessible: boolean;
  accessibilityRole: AccessibilityRole;
  accessibilityLabel: string;
  accessibilityElementsHidden: boolean;
  importantForAccessibility: string | null;
  testID: string;
  accessibilityHint: string | null;
}

export class Icon extends React.PureComponent<IconProps> {
  static propTypes: {
    name: PropTypes.Requireable<string>;
    style: PropTypes.Requireable<object>;
    accessible: PropTypes.Requireable<any>;
    accessibilityRole: PropTypes.Requireable<string>;
    accessibilityLabel: PropTypes.Requireable<string>;
    accessibilityElementsHidden: PropTypes.Requireable<boolean>;
    importantForAccessibility: PropTypes.Requireable<string>;
    testID: PropTypes.Requireable<string>;
    accessibilityHint: PropTypes.Requireable<string>;
  };

  static defaultProps: {
    name: null;
    style: {};
    accessible: undefined;
    accessibilityRole: undefined;
    accessibilityLabel: undefined;
    accessibilityElementsHidden: boolean;
    importantForAccessibility: null;
    testID: undefined;
    accessibilityHint: '';
  };

  override render() {
    const {
      name,
      style: styleProp,
      accessibilityLabel,
      accessibilityRole,
      accessible,
      accessibilityElementsHidden,
      importantForAccessibility,
      testID,
      accessibilityHint,
    } = this.props;

    // On web, render as Text component with icon font
    if (Platform.OS === 'web') {
      const unicodeChar = getIconUnicode(name);
      const isFilterIcon = name === 'filter';
      return (
        <Text
          {...testProperties(testID ?? name)}
          style={[
            styles.base,
            {
              fontFamily: 'icomoon',
              fontStyle: 'normal',
              fontWeight: 'normal',
            },
            isFilterIcon && styles.filterWeb,
            styleProp,
          ]}
          accessible={accessible}
          accessibilityElementsHidden={accessibilityElementsHidden}
          importantForAccessibility={
            importantForAccessibility as
              | 'auto'
              | 'yes'
              | 'no'
              | 'no-hide-descendants'
              | undefined
          }
          accessibilityLabel={accessibilityLabel}
          accessibilityRole={accessibilityRole}
          accessibilityHint={accessibilityHint || undefined}>
          {unicodeChar}
        </Text>
      );
    }

    // On mobile, use the original ConnectIcon component
    return (
      <ConnectIcon
        {...testProperties(testID ?? name)}
        name={name}
        accessible={accessible}
        accessibilityElementsHidden={accessibilityElementsHidden}
        importantForAccessibility={
          (importantForAccessibility || undefined) as
            | 'auto'
            | 'yes'
            | 'no'
            | 'no-hide-descendants'
            | undefined
        }
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={accessibilityRole}
        style={[styles.base, styleProp]}
        accessibilityHint={accessibilityHint || undefined}
      />
    );
  }
}

export default Icon;
